// =============================================================================
// staticSite/menuBuilder — menu strony: walidacja, budowa domyslna (z nav WP
// albo automatycznie), JEDEN renderer HTML/CSS nav dla wszystkich motywow.
//
// Zasady: kazda kategoria z wpisami jest w menu; grupa = naglowek dropdownu bez
// URL-a; max 2 poziomy; zero JS (desktop: :hover/:focus-within, mobile: checkbox
// hamburger z rozwinietymi grupami) — dziala tez w iframe z sandbox bez skryptow.
// Czysty modul (bez DB) — wspoldzielony z build-workerem i szablonami przez
// pre-render (navHtml / navCss w site.json).
// =============================================================================

import type { CategoryModel, MenuItem, PageModel, SiteMenu, SiteModel } from "./types";
import { encodePath, normalizePath, pathFromUrl, hostOf, isSiteHost } from "./urlPolicy";

const MAX_LABEL = 40;
const FLAT_MAX_CATEGORIES = 6;
const MEGA_GROUPS = 5;
const MEGA_ITEMS = 16;
const JUNK_PAGE = /^\/(sample-page|hello-world|privacy-policy-2)\/$/;

export const menuItemId = (type: MenuItem["type"], key: string) => `${type}:${key}`;

function esc(s: string): string {
  return (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function clampLabel(s: string, fallback: string): string {
  const t = (s || "").replace(/\s+/g, " ").trim();
  return (t || fallback).slice(0, MAX_LABEL);
}

// -----------------------------------------------------------------------------
// Walidacja
// -----------------------------------------------------------------------------

interface ValidatedMenu {
  menu: SiteMenu;
  warnings: string[];
}

/**
 * Sklada poprawne menu z dowolnego wejscia: max 2 poziomy, children tylko w group,
 * ref kategorii/strony musi istniec w modelu (inaczej pomijamy + warning), brak
 * duplikatow ref, etykieta 1-40 znakow, puste grupy usuwane. Zwraca null, gdy po
 * walidacji nic nie zostalo (wtedy assembler bierze fallback).
 */
export function validateMenu(input: unknown, site: Pick<SiteModel, "categories" | "pages" | "author">): ValidatedMenu | null {
  const raw = input as Partial<SiteMenu> | null;
  if (!raw || typeof raw !== "object" || !Array.isArray(raw.main)) return null;
  const warnings: string[] = [];
  const catBySlug = new Map(site.categories.map((c) => [c.slug, c]));
  const pageByPath = new Map(site.pages.filter((p) => p.kind !== "home" && p.kind !== "not_found").map((p) => [p.path, p]));

  const validateList = (items: unknown, seen: Set<string>, depth: number): MenuItem[] => {
    if (!Array.isArray(items)) return [];
    const out: MenuItem[] = [];
    for (const it of items as Partial<MenuItem>[]) {
      if (!it || typeof it !== "object" || typeof it.type !== "string") continue;
      const type = it.type as MenuItem["type"];
      if (type === "group") {
        if (depth > 0) { warnings.push(`Menu: grupa „${it.label}” zagnieżdżona w grupie — pominięta (max 2 poziomy)`); continue; }
        const children = validateList(it.children, seen, depth + 1);
        if (!children.length) continue;
        const label = clampLabel(String(it.label || ""), "Grupa");
        out.push({ id: it.id || menuItemId("group", label), label, type, children });
        continue;
      }
      let ref: string | undefined;
      let href: string | undefined;
      let fallbackLabel = "";
      if (type === "category") {
        const cat = catBySlug.get(String(it.ref || ""));
        if (!cat) { warnings.push(`Menu: kategoria „${it.ref}” nie istnieje w modelu — pominięta`); continue; }
        ref = cat.slug; fallbackLabel = cat.name;
      } else if (type === "page") {
        const pg = pageByPath.get(normalizePath(String(it.ref || "")));
        if (!pg) { warnings.push(`Menu: strona „${it.ref}” nie istnieje w modelu — pominięta`); continue; }
        ref = pg.path; fallbackLabel = pg.title;
      } else if (type === "author") {
        if (!site.author?.path) { warnings.push("Menu: strona autora nie istnieje — pominięta"); continue; }
        fallbackLabel = site.author.name;
      } else if (type === "home") {
        fallbackLabel = "Strona główna";
      } else if (type === "url") {
        href = String(it.href || "").trim();
        if (!href || !/^(https?:\/\/|\/)/.test(href)) { warnings.push(`Menu: link „${it.label}” bez poprawnego adresu — pominięty`); continue; }
        fallbackLabel = href;
      } else {
        continue;
      }
      const key = `${type}:${ref ?? href ?? ""}`;
      if (seen.has(key)) { warnings.push(`Menu: duplikat „${it.label || fallbackLabel}” — pominięty`); continue; }
      seen.add(key);
      const item: MenuItem = { id: it.id || menuItemId(type, ref ?? href ?? type), label: clampLabel(String(it.label || ""), fallbackLabel), type };
      if (ref) item.ref = ref;
      if (href) item.href = href;
      out.push(item);
    }
    return out;
  };

  const mainSeen = new Set<string>();
  const main = validateList(raw.main, mainSeen, 0);
  const footer = Array.isArray(raw.footer) ? validateList(raw.footer, new Set<string>(), 0) : undefined;
  if (!main.length) return null;
  const source: SiteMenu["source"] = (["ai", "wp", "manual", "auto"] as const).includes(raw.source as SiteMenu["source"]) ? (raw.source as SiteMenu["source"]) : "manual";
  return { menu: { main, footer: footer?.length ? footer : undefined, source, generated_at: raw.generated_at }, warnings };
}

// -----------------------------------------------------------------------------
// Budowa domyslna
// -----------------------------------------------------------------------------

export function sortedCategories(site: Pick<SiteModel, "categories">): CategoryModel[] {
  return site.categories.filter((c) => c.count > 0).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

function categoryItem(c: CategoryModel): MenuItem {
  return { id: menuItemId("category", c.slug), label: c.name, type: "category", ref: c.slug };
}

function extraItems(site: Pick<SiteModel, "pages" | "author">): MenuItem[] {
  const items: MenuItem[] = [];
  const subpages = site.pages.filter((p) => (p.kind === "subpage" || p.kind === "legacy_page") && !JUNK_PAGE.test(p.path));
  const about = subpages.find((p) => /o-nas|o-mnie|about|kim-jest/i.test(p.path));
  const contact = subpages.find((p) => /kontakt|contact/i.test(p.path));
  if (about) items.push({ id: menuItemId("page", about.path), label: about.title, type: "page", ref: about.path });
  if (site.author?.path) items.push({ id: menuItemId("author", "author"), label: site.author.name, type: "author" });
  if (contact) items.push({ id: menuItemId("page", contact.path), label: contact.title, type: "page", ref: contact.path });
  return items;
}

/** Wszystkie kategorie (<= 6 plasko, wiecej w jednej grupie „Kategorie") + o nas / autor / kontakt. Nigdy nie pomija kategorii. */
export function buildMenuAuto(site: Pick<SiteModel, "categories" | "pages" | "author">): SiteMenu {
  const cats = sortedCategories(site).map(categoryItem);
  const main: MenuItem[] = cats.length <= FLAT_MAX_CATEGORIES
    ? cats
    : [{ id: menuItemId("group", "kategorie"), label: "Kategorie", type: "group", children: cats }];
  main.push(...extraItems(site));
  return { main, source: "auto", generated_at: new Date().toISOString() };
}

// -----------------------------------------------------------------------------
// Z nav WordPressa (HTML strony glownej) — regex, bez DOM
// -----------------------------------------------------------------------------

interface ParsedLi { label: string; href: string | null; children: ParsedLi[] }

/** Wycina pierwszy <nav>…</nav> (albo <ul class="…menu…">) i parsuje <li> z zagniezdzonymi <ul>. */
function parseNav(html: string): ParsedLi[] {
  const navMatch = html.match(/<nav\b[^>]*>([\s\S]*?)<\/nav>/i);
  const ulMatch = html.match(/<ul\b[^>]*class="[^"]*\bmenu\b[^"]*"[^>]*>[\s\S]*?<\/ul>/i);
  let scope = navMatch ? navMatch[1] : ulMatch ? ulMatch[0] : "";
  // W <nav> szukamy pierwszej listy z linkami
  const firstUl = scope.search(/<ul\b/i);
  if (firstUl < 0) return [];
  scope = scope.slice(firstUl);
  const { items } = parseUl(scope, 0);
  return items;
}

/** Parser <ul> ze stosem — zwraca elementy i pozycje konca. */
function parseUl(s: string, start: number): { items: ParsedLi[]; end: number } {
  const items: ParsedLi[] = [];
  const tagRe = /<(\/?)(ul|li)\b[^>]*>/gi;
  tagRe.lastIndex = start;
  // pomijamy otwierajacy <ul>
  const open = tagRe.exec(s);
  if (!open || open[1] === "/" || open[2].toLowerCase() !== "ul") return { items, end: s.length };
  let current: ParsedLi | null = null;
  let liStart = -1;
  let m: RegExpExecArray | null;
  while ((m = tagRe.exec(s))) {
    const closing = m[1] === "/";
    const tag = m[2].toLowerCase();
    if (tag === "ul" && !closing) {
      // zagniezdzona lista w biezacym <li>
      const sub = parseUl(s, m.index);
      if (current) current.children.push(...sub.items);
      tagRe.lastIndex = sub.end;
      continue;
    }
    if (tag === "ul" && closing) {
      if (current) finalizeLi(current, s.slice(liStart, m.index));
      return { items, end: m.index + m[0].length };
    }
    if (tag === "li" && !closing) {
      if (current) finalizeLi(current, s.slice(liStart, m.index));
      current = { label: "", href: null, children: [] };
      items.push(current);
      liStart = m.index + m[0].length;
      continue;
    }
    if (tag === "li" && closing && current) {
      finalizeLi(current, s.slice(liStart, m.index));
      current = null;
    }
  }
  if (current) finalizeLi(current, s.slice(liStart));
  return { items, end: s.length };
}

function finalizeLi(li: ParsedLi, inner: string): void {
  if (li.label) return;
  // Tylko wlasna zawartosc <li> (bez zagniezdzonego <ul>)
  const own = inner.split(/<ul\b/i)[0];
  const a = own.match(/<a\b[^>]*>([\s\S]*?)<\/a>/i);
  const hrefM = own.match(/<a\b[^>]*href\s*=\s*"([^"]*)"/i);
  const text = (a ? a[1] : own).replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
  li.label = text;
  li.href = hrefM ? hrefM[1].trim() : null;
}

/**
 * Menu z nav WordPressa. Pozycje mapowane po URL do kategorii / stron / autora;
 * nieznane URL-e tego samego hosta -> url; obce hosty pomijane. <li> z zagniezdzonym
 * <ul> -> grupa (jesli rodzic ma wlasny URL kategorii/strony -> rodzic zostaje
 * pozycja, dzieci ida osobno na poziom 1, bo grupa nie ma URL-a). Kategorie
 * nieobecne w nav WP dopinane do grupy kategorii albo „Pozostałe". null gdy < 2
 * rozpoznanych pozycji.
 */
/** Deterministycznie z theme_seed: autor do menu glownego albo do stopki (footprint). */
function authorGoesToFooter(themeSeed: string | null): boolean {
  const b = parseInt((themeSeed || "0").slice(16, 18), 16) || 0;
  return b % 2 === 1;
}

/**
 * Menu z grup zaproponowanych przez AI (static_menu_grouping) — normalizacja w kodzie:
 * nieznane slugi pomijane, duplikaty usuwane, nieprzypisane kategorie -> „Pozostałe",
 * o-nas/autor/kontakt dopina kod (autor do main albo footer wg theme_seed — footprint).
 */
export function buildMenuFromGroups(
  groups: Array<{ label?: unknown; category_slugs?: unknown }>,
  scope: Pick<SiteModel, "categories" | "pages" | "author">,
  themeSeed: string | null,
): SiteMenu {
  const cats = sortedCategories(scope);
  const bySlug = new Map(cats.map((c) => [c.slug, c]));
  const used = new Set<string>();
  const main: MenuItem[] = [];
  for (const g of groups || []) {
    const label = String(g.label || "").replace(/\s+/g, " ").trim().slice(0, 40);
    const slugs = Array.isArray(g.category_slugs) ? g.category_slugs.map((s) => String(s).trim().toLowerCase()) : [];
    if (!label) continue; // grupa bez etykiety -> jej kategorie trafia do „Pozostałe"
    const children: MenuItem[] = [];
    for (const slug of slugs) {
      const c = bySlug.get(slug);
      if (!c || used.has(slug) || children.some((ch) => ch.ref === slug)) continue;
      children.push(categoryItem(c));
    }
    if (!children.length) continue;
    for (const ch of children) used.add(ch.ref!);
    main.push({ id: menuItemId("group", label), label, type: "group", children });
  }
  const rest = cats.filter((c) => !used.has(c.slug)).map(categoryItem);
  if (rest.length) main.push({ id: menuItemId("group", "pozostale"), label: "Pozostałe", type: "group", children: rest });

  // Autor / o nas / kontakt — dopina kod, nie AI
  const subpages = scope.pages.filter((p) => (p.kind === "subpage" || p.kind === "legacy_page") && !/^\/(sample-page|hello-world)\/$/.test(p.path));
  const about = subpages.find((p) => /o-nas|o-mnie|about|kim-jest/i.test(p.path));
  const contact = subpages.find((p) => /kontakt|contact/i.test(p.path));
  const footer: MenuItem[] = [];
  if (about) main.push({ id: menuItemId("page", about.path), label: about.title, type: "page", ref: about.path });
  if (scope.author?.path) {
    const item: MenuItem = { id: menuItemId("author", "author"), label: scope.author.name, type: "author" };
    (authorGoesToFooter(themeSeed) ? footer : main).push(item);
  }
  if (contact) main.push({ id: menuItemId("page", contact.path), label: contact.title, type: "page", ref: contact.path });
  return { main, footer: footer.length ? footer : undefined, source: "ai", generated_at: new Date().toISOString() };
}


export function buildMenuFromWp(html: string, site: Pick<SiteModel, "categories" | "pages" | "author" | "domain">): SiteMenu | null {
  const parsed = parseNav(html);
  if (!parsed.length) return null;
  const catByPath = new Map(site.categories.map((c) => [normalizePath(c.path), c]));
  const pageByPath = new Map(site.pages.filter((p) => p.kind !== "home" && p.kind !== "not_found").map((p) => [p.path, p]));
  const authorPath = site.author?.path ? normalizePath(site.author.path) : null;

  const toItem = (li: ParsedLi): MenuItem | null => {
    const href = (li.href || "").trim();
    if (!href || href === "#") return null;
    const host = hostOf(href);
    if (host && !isSiteHost(host, site.domain)) return null;
    let path: string;
    try { path = normalizePath(/^https?:\/\//i.test(href) ? pathFromUrl(href) : href.split("#")[0].split("?")[0]); } catch { return null; }
    if (path === "/") return { id: menuItemId("home", "home"), label: clampLabel(li.label, "Strona główna"), type: "home" };
    const cat = catByPath.get(path);
    if (cat) return { id: menuItemId("category", cat.slug), label: clampLabel(li.label, cat.name), type: "category", ref: cat.slug };
    if (authorPath && path === authorPath) return { id: menuItemId("author", "author"), label: clampLabel(li.label, site.author!.name), type: "author" };
    const pg = pageByPath.get(path);
    if (pg) return { id: menuItemId("page", pg.path), label: clampLabel(li.label, pg.title), type: "page", ref: pg.path };
    // Archiwa WP (kategoria bez tresci, tag, autor, paginacja) — na stronie statycznej to 301/404, nie linkujemy
    if (/^\/(category|kategoria|tag|author|page)\//i.test(path) || /\/page\/\d+\/$/.test(path)) return null;
    return { id: menuItemId("url", path), label: clampLabel(li.label, path), type: "url", href: path };
  };

  const main: MenuItem[] = [];
  let recognized = 0;
  for (const li of parsed) {
    const self = toItem(li);
    const children = li.children.map(toItem).filter((x): x is MenuItem => Boolean(x) && x!.type !== "group");
    if (li.children.length && (!self || self.type === "url")) {
      // rodzic bez URL-a (href="#") -> grupa
      if (children.length) main.push({ id: menuItemId("group", li.label || "grupa"), label: clampLabel(li.label, "Więcej"), type: "group", children });
      recognized += children.filter((c) => c.type !== "url").length;
      continue;
    }
    if (self) { main.push(self); if (self.type !== "url") recognized++; }
    for (const c of children) { main.push(c); if (c.type !== "url") recognized++; }
  }
  if (recognized < 2) return null;

  // Kazda kategoria z wpisami musi byc w menu
  const present = new Set<string>();
  const walk = (items: MenuItem[]) => { for (const it of items) { if (it.type === "category" && it.ref) present.add(it.ref); if (it.children) walk(it.children); } };
  walk(main);
  const missing = sortedCategories(site).filter((c) => !present.has(c.slug)).map(categoryItem);
  if (missing.length) {
    const catGroup = main.find((it) => it.type === "group" && it.children?.every((c) => c.type === "category"));
    if (catGroup) catGroup.children!.push(...missing);
    else main.push({ id: menuItemId("group", "pozostale"), label: "Pozostałe", type: "group", children: missing });
  }

  const validated = validateMenu({ main, source: "wp", generated_at: new Date().toISOString() }, site);
  return validated?.menu ?? null;
}

// -----------------------------------------------------------------------------
// Render — jeden HTML/CSS nav dla wszystkich motywow (prefix klas per motyw)
// -----------------------------------------------------------------------------

interface NavRenderOptions {
  /** Prefiks klas CSS: "sn-" (Astro) albo prefiks wariacji motywu editorial */
  prefix: string;
  /** Sciezka biezacej strony -> aria-current="page" */
  current?: string;
  /** Sciezka href pozycji (encodePath) */
}

function menuHref(item: MenuItem, site: Pick<SiteModel, "categories" | "author">): string | null {
  switch (item.type) {
    case "category": { const c = site.categories.find((x) => x.slug === item.ref); return c ? encodePath(c.path) : null; }
    case "page": return item.ref ? encodePath(item.ref) : null;
    case "author": return site.author?.path ? encodePath(site.author.path) : null;
    case "home": return "/";
    case "url": return item.href || null;
    default: return null;
  }
}

export function countMenuItems(items: MenuItem[]): number {
  return items.reduce((n, it) => n + (it.type === "group" ? countMenuItems(it.children || []) : 1), 0);
}

export function isMegaMenu(menu: SiteMenu): boolean {
  const groups = menu.main.filter((it) => it.type === "group").length;
  return groups >= MEGA_GROUPS || countMenuItems(menu.main) >= MEGA_ITEMS;
}

/**
 * <nav data-site-nav> z checkboxem hamburgera (mobile) i dropdownami (desktop).
 * Zero JS. Wszystkie pozycje w tej samej strukturze na obu widokach.
 */
export function renderNavHtml(menu: SiteMenu, site: Pick<SiteModel, "categories" | "author">, opts: NavRenderOptions): string {
  const p = opts.prefix;
  const current = opts.current ? normalizePath(opts.current) : null;
  const link = (it: MenuItem) => {
    const href = menuHref(it, site);
    if (!href) return "";
    const isCurrent = current && (it.type === "url" ? false : normalizePath(decodeURIComponent(href)) === current);
    return `<a href="${esc(href)}"${isCurrent ? ` aria-current="page" class="${p}nav-current"` : ""}>${esc(it.label)}</a>`;
  };
  const li = (it: MenuItem): string => {
    if (it.type === "group") {
      const inner = (it.children || []).map((c) => { const a = link(c); return a ? `<li class="${p}nav-item">${a}</li>` : ""; }).join("");
      if (!inner) return "";
      return `<li class="${p}nav-group" tabindex="0"><span class="${p}nav-group__label">${esc(it.label)}</span><ul class="${p}nav-sub">${inner}</ul></li>`;
    }
    const a = link(it);
    return a ? `<li class="${p}nav-item">${a}</li>` : "";
  };
  const mega = isMegaMenu(menu) ? ` ${p}nav--mega` : "";
  return `<nav data-site-nav class="${p}nav${mega}" aria-label="Menu">` +
    `<input type="checkbox" id="${p}nav-toggle" class="${p}nav-toggle" aria-label="Menu">` +
    `<label for="${p}nav-toggle" class="${p}nav-burger" aria-hidden="true"><span></span><span></span><span></span></label>` +
    `<ul class="${p}nav-list">${menu.main.map(li).join("")}</ul></nav>`;
}

/** Stopka: grupy jako kolumny z naglowkiem, pozycje luzem jako jedna kolumna „Strony". */
export function renderFooterMenuHtml(items: MenuItem[] | undefined, site: Pick<SiteModel, "categories" | "author">, prefix: string): string {
  if (!items?.length) return "";
  const p = prefix;
  const linkLi = (it: MenuItem) => { const href = menuHref(it, site); return href ? `<li><a href="${esc(href)}">${esc(it.label)}</a></li>` : ""; };
  const cols: string[] = [];
  const loose: MenuItem[] = [];
  for (const it of items) {
    if (it.type === "group") cols.push(`<div class="${p}fnav-col"><h4>${esc(it.label)}</h4><ul>${(it.children || []).map(linkLi).join("")}</ul></div>`);
    else loose.push(it);
  }
  if (loose.length) cols.push(`<div class="${p}fnav-col"><h4>Strony</h4><ul>${loose.map(linkLi).join("")}</ul></div>`);
  return `<div data-site-footer-nav class="${p}fnav">${cols.join("")}</div>`;
}

interface NavCssTokens {
  /** kolory jako wartosci CSS (moga byc var(--x)) */
  text: string;
  muted: string;
  bg: string;
  surface: string;
  border: string;
  accent: string;
  radius: string;
  /** wysokosc naglowka (mobile drawer zaczyna sie pod nim) */
  headerHeight: string;
  /** breakpoint mobile (px) */
  breakpoint?: number;
  /** font pozycji (opcjonalnie) */
  font?: string;
}

/** CSS nav — desktop dropdown/mega + mobile drawer (checkbox). Jedno zrodlo dla 3 motywow. */
export function renderNavCss(prefix: string, t: NavCssTokens): string {
  const p = prefix;
  const bp = t.breakpoint ?? 900;
  const font = t.font ? `font-family:${t.font};` : "";
  return [
    `.${p}nav{position:relative;min-width:0;flex:1}`,
    `.${p}nav-toggle{position:absolute;opacity:0;width:0;height:0;pointer-events:none}`,
    `.${p}nav-burger{display:none;width:40px;height:40px;cursor:pointer;flex-direction:column;justify-content:center;gap:5px;padding:8px;margin-left:auto;border-radius:${t.radius}}`,
    `.${p}nav-burger span{display:block;height:2px;background:${t.text};border-radius:2px;transition:transform .2s,opacity .2s}`,
    `.${p}nav-list,.${p}nav-sub{list-style:none;margin:0;padding:0}`,
    `.${p}nav-list{display:flex;flex-wrap:wrap;gap:.15rem;align-items:center}`,
    `.${p}nav-list a,.${p}nav-group__label{display:block;padding:.45rem .7rem;border-radius:${t.radius};color:${t.muted};font-size:.92rem;font-weight:500;white-space:nowrap;text-decoration:none;${font}}`,
    `.${p}nav-list a:hover,.${p}nav-list a.${p}nav-current,.${p}nav-group:hover>.${p}nav-group__label,.${p}nav-group:focus-within>.${p}nav-group__label{color:${t.text};background:${t.surface}}`,
    `.${p}nav-group{position:relative;cursor:default;outline:none}`,
    `.${p}nav-group__label::after{content:"";display:inline-block;width:.45em;height:.45em;margin-left:.45em;border-right:1.5px solid currentColor;border-bottom:1.5px solid currentColor;transform:translateY(-.15em) rotate(45deg);opacity:.7}`,
    `.${p}nav-sub{display:none;position:absolute;top:100%;left:0;z-index:40;min-width:240px;padding:.4rem;background:${t.bg};border:1px solid ${t.border};border-radius:${t.radius};box-shadow:0 14px 40px -18px rgba(0,0,0,.45)}`,
    `.${p}nav-group:hover>.${p}nav-sub,.${p}nav-group:focus-within>.${p}nav-sub{display:block}`,
    `.${p}nav-sub a{white-space:normal;font-size:.88rem;padding:.4rem .6rem}`,
    `.${p}nav--mega .${p}nav-sub{min-width:min(560px,80vw);display:none;grid-template-columns:1fr 1fr;gap:0 .5rem}`,
    `.${p}nav--mega .${p}nav-group:hover>.${p}nav-sub,.${p}nav--mega .${p}nav-group:focus-within>.${p}nav-sub{display:grid}`,
    `.${p}nav-group:last-child>.${p}nav-sub{left:auto;right:0}`,
    `@media (max-width:${bp}px){`,
    // position:static — drawer pozycjonowany wzgledem <header> (sticky/relative), NIE fixed:
    // backdrop-filter/transform na headerze robi z niego containing block dla fixed i ucina drawer.
    `.${p}nav{flex:0 0 auto;margin-left:auto;position:static}`,
    `.${p}nav-burger{display:flex}`,
    `.${p}nav-list{display:none;position:absolute;left:0;right:0;top:100%;max-height:calc(100dvh - ${t.headerHeight});z-index:50;overflow:auto;flex-direction:column;flex-wrap:nowrap;align-items:stretch;gap:0;padding:.75rem 1rem 2rem;background:${t.bg};border-top:1px solid ${t.border};box-shadow:0 20px 40px -20px rgba(0,0,0,.5)}`,
    `.${p}nav-toggle:checked~.${p}nav-list{display:flex}`,
    `.${p}nav-toggle:checked~.${p}nav-burger span:nth-child(1){transform:translateY(7px) rotate(45deg)}`,
    `.${p}nav-toggle:checked~.${p}nav-burger span:nth-child(2){opacity:0}`,
    `.${p}nav-toggle:checked~.${p}nav-burger span:nth-child(3){transform:translateY(-7px) rotate(-45deg)}`,
    `.${p}nav-list a{white-space:normal;padding:.6rem .5rem;font-size:1rem;border-bottom:1px solid ${t.border}}`,
    `.${p}nav-group__label{padding:.9rem .5rem .3rem;font-size:.72rem;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:${t.accent};cursor:default}`,
    `.${p}nav-group__label::after{content:none}`,
    `.${p}nav-group:hover>.${p}nav-group__label,.${p}nav-group:focus-within>.${p}nav-group__label{background:transparent}`,
    `.${p}nav-sub,.${p}nav--mega .${p}nav-sub,.${p}nav-group:hover>.${p}nav-sub,.${p}nav--mega .${p}nav-group:hover>.${p}nav-sub{display:block;position:static;min-width:0;padding:0 0 .4rem;border:0;box-shadow:none;background:transparent}`,
    `}`,
  ].join("\n");
}
